import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  CheckCircle,
  BrainCircuit,
  Network,
  Code,
  Database,
  Lock,
  Wrench,
  Zap,
  Globe,
  FileText,
  Settings
} from 'lucide-react';
import Navbar from './Navbar';

const BespokeOrchestration = () => {
  const navigate = useNavigate();
  const [showStickyButton, setShowStickyButton] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;

      const scrollPercentage = scrollPosition / (documentHeight - windowHeight);
      setShowStickyButton(scrollPercentage > 0.5);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToContact = () => {
    navigate('/?subject=Bespoke%20Intelligence%20Audit', { replace: true });
    setTimeout(() => {
      const element = document.getElementById('contact');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };

  const benefits = [
    {
      icon: Network,
      title: 'If It Has an API, We Can Build Its Brain',
      description: 'Legacy ERP, industry-specific software, proprietary systems—if it exposes data via API, we can orchestrate it into your Intelligence Layer.'
    },
    {
      icon: Wrench,
      title: 'Custom Business Logic',
      description: 'Your workflows are unique. We design contextual orchestrations and synthesis prompts tailored to your exact operational requirements.'
    },
    {
      icon: Database,
      title: 'Unified Data Context',
      description: 'Connect disparate systems that were never designed to talk—CRM, inventory, production, logistics, HR—into a single queryable intelligence layer.'
    },
    {
      icon: FileText,
      title: 'Industry-Specific Intelligence',
      description: 'Manufacturing, healthcare, legal, logistics—we build contextual fact sheets for specialized industries with unique compliance and data requirements.'
    },
    {
      icon: Lock,
      title: 'Client-Controlled Deployment',
      description: 'Deploy with your chosen AI model (self-hosted like Ollama, or cloud-based like Claude/GPT under your API keys). No data sent to us. You own the entire intelligence stack.'
    },
    {
      icon: Zap,
      title: 'Scalable Architecture',
      description: 'Start with one workflow, expand to dozens. Our context orchestration framework grows with your business needs.'
    }
  ];

  const idealFor = [
    {
      icon: Code,
      title: 'Legacy System Integration',
      scenarios: [
        'Mainframe or on-prem ERP systems',
        'Custom-built software without modern APIs',
        'Industry-specific software (dental, legal, manufacturing)',
        'Proprietary data formats and protocols'
      ]
    },
    {
      icon: Globe,
      title: 'Multi-System Orchestration',
      scenarios: [
        'Connecting 5+ disparate software platforms',
        'Cross-department data synthesis (sales + ops + finance)',
        'Supply chain visibility across vendors',
        'Real-time production intelligence'
      ]
    },
    {
      icon: Settings,
      title: 'Complex Business Rules',
      scenarios: [
        'Multi-step approval workflows',
        'Compliance-driven data synthesis',
        'Custom calculation logic',
        'Dynamic pricing or inventory optimization'
      ]
    }
  ];

  const process = [
    {
      step: '01',
      title: 'Discovery Audit',
      duration: '1-2 weeks',
      description: 'Deep dive into your software ecosystem. We map APIs, data structures, business logic, and edge cases with read-only access.',
      deliverables: ['System architecture map', 'API capability assessment', 'Data flow diagram', 'Complexity estimate']
    },
    {
      step: '02',
      title: 'Orchestration Design',
      duration: '2-4 weeks',
      description: 'We architect the contextual workflows and AI synthesis prompts. This includes error handling, fallback logic, and compliance requirements.',
      deliverables: ['Workflow blueprints', 'Synthesis prompt library', 'Integration test plan', 'Deployment architecture']
    },
    {
      step: '03',
      title: 'Development & Testing',
      duration: '4-8 weeks',
      description: 'Build and validate the intelligence layer against your real data. Iterative testing with your team to refine accuracy and business logic.',
      deliverables: ['Functional context workflows', 'Configured AI integration', 'Test results and accuracy reports', 'Documentation']
    },
    {
      step: '04',
      title: 'Deployment & Training',
      duration: '1-2 weeks',
      description: 'Launch with your chosen deployment model. Train your team on natural language queries, workflow maintenance, and system monitoring.',
      deliverables: ['Production deployment', 'Team training sessions', 'Maintenance documentation', 'Support handoff']
    }
  ];

  const caseStudies = [
    {
      industry: 'Manufacturing',
      challenge: 'Production line data siloed across legacy MES, inventory management, and quality control systems.',
      solution: 'Built custom context workflows connecting 6 systems via SOAP and REST APIs. AI synthesizes real-time production intelligence.',
      result: 'Reduced production planning time by 60%. Natural language queries like "Which line has the most downtime this week?" answered instantly.'
    },
    {
      industry: 'Healthcare (MSO)',
      challenge: 'Patient data scattered across EHR, billing system, scheduling software, and internal documentation.',
      solution: 'HIPAA-compliant orchestration with private AI model on-premise. Read-only access to synthesize patient care context without data export.',
      result: 'Clinicians get comprehensive patient fact sheets in under 30 seconds. Zero PHI transmitted to external systems.'
    },
    {
      industry: 'Logistics',
      challenge: 'Shipment visibility requires checking 4 different vendor portals, internal TMS, and customer CRM.',
      solution: 'Custom API integrations into unified intelligence layer. Real-time shipment status synthesis with exception flagging.',
      result: 'Customer service team responds to shipment inquiries in under 1 minute instead of 15+ minutes.'
    }
  ];

  return (
    <>
      <Navbar />

      <div className="min-h-screen bg-white">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-amber-50 to-white pt-32 pb-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <button
              onClick={() => navigate('/')}
              className="flex items-center gap-2 text-amber-600 hover:text-amber-700 font-semibold mb-8 transition-colors"
            >
              <ArrowLeft className="h-5 w-5" />
              Back to Home
            </button>

            <div className="text-center max-w-4xl mx-auto">
              <div className="bg-amber-100 w-24 h-24 rounded-2xl flex items-center justify-center mx-auto mb-8">
                <BrainCircuit className="h-12 w-12 text-amber-600" />
              </div>

              <div className="inline-block bg-amber-500 text-white text-sm font-bold px-4 py-2 rounded-full mb-6">
                CUSTOM BUILD
              </div>

              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
                Bespoke Intelligence
              </h1>

              <p className="text-2xl text-amber-600 font-medium mb-6">
                If It Has an API, We Can Build Its Brain.
              </p>

              <p className="text-xl text-gray-600 leading-relaxed mb-8">
                Bespoke AI intelligence for specialized industries and unique datasets. We design custom
                workflows and synthesis prompts tailored to your exact business logic—then deploy with your
                chosen AI model (self-hosted like Ollama, or cloud-based like Claude/GPT under your API keys).
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button
                  onClick={scrollToContact}
                  className="bg-amber-500 hover:bg-amber-600 text-white px-8 py-4 rounded-lg font-semibold text-lg transition-all duration-200 shadow-lg hover:shadow-xl"
                >
                  Request Custom Audit
                </button>
              </div>

              <div className="mt-8 flex items-center justify-center gap-2 text-sm text-gray-500">
                <Lock className="h-4 w-4" />
                <span>Client-controlled deployment · Full ownership · Your AI choice</span>
              </div>
            </div>
          </div>
        </section>

        {/* Benefits Grid */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Why Bespoke Intelligence?
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                When pre-engineered solutions don't fit—we build exactly what you need.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {benefits.map((benefit, index) => {
                const IconComponent = benefit.icon;
                return (
                  <div
                    key={index}
                    className="bg-white p-8 rounded-2xl border border-gray-100 hover:border-amber-200 hover:shadow-xl transition-all duration-300"
                  >
                    <div className="bg-amber-100 w-12 h-12 rounded-lg flex items-center justify-center mb-4">
                      <IconComponent className="h-6 w-6 text-amber-600" />
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-3">
                      {benefit.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed">
                      {benefit.description}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Ideal For Section */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Is Bespoke Intelligence Right for You?
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                These scenarios are ideal for bespoke intelligence layer development.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {idealFor.map((category, index) => {
                const IconComponent = category.icon;
                return (
                  <div
                    key={index}
                    className="bg-gradient-to-br from-amber-50 to-white p-8 rounded-2xl border border-amber-100"
                  >
                    <div className="bg-amber-100 w-12 h-12 rounded-lg flex items-center justify-center mb-4">
                      <IconComponent className="h-6 w-6 text-amber-600" />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-6">
                      {category.title}
                    </h3>
                    <ul className="space-y-3">
                      {category.scenarios.map((scenario, idx) => (
                        <li key={idx} className="flex items-start gap-3">
                          <CheckCircle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
                          <span className="text-gray-700">{scenario}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Process Timeline */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                The Bespoke Intelligence Process
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Typical timeline: 8-16 weeks from discovery to production deployment.
              </p>
            </div>

            <div className="space-y-8">
              {process.map((phase, index) => (
                <div
                  key={index}
                  className="bg-white p-8 rounded-2xl border border-gray-100 hover:shadow-lg transition-shadow"
                >
                  <div className="flex flex-col md:flex-row gap-6">
                    <div className="flex-shrink-0">
                      <div className="bg-amber-500 text-white w-16 h-16 rounded-full flex items-center justify-center text-2xl font-bold">
                        {phase.step}
                      </div>
                    </div>
                    <div className="flex-1">
                      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4">
                        <h3 className="text-2xl font-bold text-gray-900">
                          {phase.title}
                        </h3>
                        <div className="text-sm font-semibold text-amber-600 bg-amber-50 px-3 py-1 rounded-full inline-block mt-2 md:mt-0">
                          {phase.duration}
                        </div>
                      </div>
                      <p className="text-gray-600 leading-relaxed mb-4">
                        {phase.description}
                      </p>
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <h4 className="text-sm font-semibold text-gray-700 mb-2">Deliverables:</h4>
                        <ul className="grid md:grid-cols-2 gap-2">
                          {phase.deliverables.map((deliverable, idx) => (
                            <li key={idx} className="flex items-center gap-2 text-sm text-gray-600">
                              <CheckCircle className="h-4 w-4 text-amber-600 flex-shrink-0" />
                              <span>{deliverable}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Case Studies */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Bespoke Intelligence in Action
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Real-world examples of bespoke intelligence layers we've built.
              </p>
            </div>

            <div className="space-y-8">
              {caseStudies.map((study, index) => (
                <div
                  key={index}
                  className="bg-gradient-to-br from-gray-50 to-white p-8 rounded-2xl border border-gray-100"
                >
                  <div className="inline-block bg-amber-500 text-white text-xs font-bold px-3 py-1 rounded-full mb-4">
                    {study.industry}
                  </div>

                  <div className="grid md:grid-cols-3 gap-6">
                    <div>
                      <h4 className="text-sm font-bold text-gray-500 uppercase mb-2">Challenge</h4>
                      <p className="text-gray-700">{study.challenge}</p>
                    </div>
                    <div>
                      <h4 className="text-sm font-bold text-gray-500 uppercase mb-2">Solution</h4>
                      <p className="text-gray-700">{study.solution}</p>
                    </div>
                    <div>
                      <h4 className="text-sm font-bold text-gray-500 uppercase mb-2">Result</h4>
                      <p className="text-gray-700 font-semibold text-amber-600">{study.result}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Privacy Guarantee */}
        <section className="py-20 bg-gradient-to-br from-amber-50 to-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <Lock className="h-16 w-16 text-amber-600 mx-auto mb-6" />
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              Your Business. Your Data. Your Intelligence Layer.
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed mb-8">
              Every custom orchestration is deployed with your chosen AI model (self-hosted or cloud-based under your API keys).
              We never store, export, or transmit your operational data. All API connections are read-only
              during development. The intelligence layer you pay for is the intelligence layer you own—completely.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-gray-600">
              <div className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-amber-600" />
                <span>Read-Only Development</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-amber-600" />
                <span>Zero Data Retention</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-amber-600" />
                <span>Client-Controlled AI</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-amber-600" />
                <span>Complete Ownership</span>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Ready to Build Your Bespoke Intelligence Layer?
            </h2>
            <p className="text-xl text-gray-600 mb-8">
              Let's audit your software ecosystem and design a bespoke orchestration strategy.
            </p>
            <button
              onClick={scrollToContact}
              className="bg-amber-500 hover:bg-amber-600 text-white px-8 py-4 rounded-lg font-semibold text-lg transition-all duration-200 shadow-lg hover:shadow-xl"
            >
              Request Custom Audit
            </button>
            <p className="text-sm text-gray-500 mt-4">
              Discovery audits are typically 1-2 weeks with a fixed-fee proposal.
            </p>
          </div>
        </section>

        {/* Sticky CTA Button */}
        {showStickyButton && (
          <button
            onClick={scrollToContact}
            className="fixed bottom-8 right-8 bg-amber-500 hover:bg-amber-600 text-white px-6 py-3 rounded-full font-semibold shadow-2xl hover:shadow-3xl transition-all duration-300 animate-pulse z-50"
          >
            Get Started →
          </button>
        )}
      </div>
    </>
  );
};

export default BespokeOrchestration;
